import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import MascotLoader from '../components/MascotLoader';
import { MASCOT_URL } from '../assets/mascot';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { FileText, Clock, CheckCircle, Users, LogOut, Search, MapPin, Calendar, Bell, Download, FolderOpen, Building2, Mail, PenTool, Euro, AlertCircle, Eye, Filter, RefreshCw, FileDown, ChevronLeft, ChevronRight, Edit, X, Moon, Sun, Upload, Trash2, Video } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO = '/kardif-logo.png';

function SecretaryDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const [interventions, setInterventions] = useState([]);
  const [allInterventions, setAllInterventions] = useState([]);
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [pendingFranchises, setPendingFranchises] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [missions, setMissions] = useState([]);
  
  // Filtres
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTechnician, setFilterTechnician] = useState('all');
  const [filterDate, setFilterDate] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Edit mission dialog
  const [editingMission, setEditingMission] = useState(null);
  const [showEditMission, setShowEditMission] = useState(false);
  const [donneursOrdre, setDonneursOrdre] = useState([]);
  const [showNewDossier, setShowNewDossier] = useState(false);
  const [creatingDossier, setCreatingDossier] = useState(false);
  const [newDossier, setNewDossier] = useState({
    technician_id: '',
    nom_sinistre: '',
    adresse_complete: '',
    client_telephone: '',
    numero_sinistre: '',
    numero_dossier: '',
    nom_assurance: '',
    donneur_ordre_id: '',
    resume_constats: '',
    pieces_jointes: [],
  });
  const [uploadingDossierPJ, setUploadingDossierPJ] = useState(false);

  const handleCreateDossier = async () => {
    if (!newDossier.technician_id) { toast.error('Choisissez un technicien'); return; }
    if (!newDossier.nom_sinistre && !newDossier.adresse_complete) { toast.error('Renseignez au moins le sinistré ou l\'adresse'); return; }
    setCreatingDossier(true);
    try {
      const payload = { ...newDossier, current_step: 1 };
      if (!payload.donneur_ordre_id) delete payload.donneur_ordre_id;
      await axios.post(`${API}/drafts`, payload);
      toast.success('Dossier créé et transmis au technicien');
      setShowNewDossier(false);
      setNewDossier({ technician_id: '', nom_sinistre: '', adresse_complete: '', client_telephone: '', numero_sinistre: '', numero_dossier: '', nom_assurance: '', donneur_ordre_id: '', resume_constats: '', pieces_jointes: [] });
      fetchData();
    } catch (e) { toast.error('Erreur création du dossier'); }
    finally { setCreatingDossier(false); }
  };

  // Notifications temps réel
  const [newNotifPulse, setNewNotifPulse] = useState(false);
  const prevNotifCount = useRef(null);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.36);
      // deuxieme note (ding-dong)
      const o2 = ctx.createOscillator(); const g2 = ctx.createGain();
      o2.connect(g2); g2.connect(ctx.destination); o2.type = 'sine'; o2.frequency.value = 1174;
      g2.gain.setValueAtTime(0.0001, ctx.currentTime + 0.18);
      g2.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.2);
      g2.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
      o2.start(ctx.currentTime + 0.18); o2.stop(ctx.currentTime + 0.56);
    } catch (e) { /* audio non dispo */ }
  };

  const pollNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      const notifs = res.data.filter(n => ['rapport_final_ready', 'new_intervention', 'new_mission'].includes(n.type));
      setNotifications(notifs);
      if (prevNotifCount.current !== null && notifs.length > prevNotifCount.current) {
        const diff = notifs.length - prevNotifCount.current;
        playBeep();
        setNewNotifPulse(true);
        setTimeout(() => setNewNotifPulse(false), 8000);
        toast.success(`${diff} nouvelle${diff > 1 ? 's' : ''} intervention${diff > 1 ? 's' : ''} reçue${diff > 1 ? 's' : ''} !`, { icon: '🔔' });
      }
      prevNotifCount.current = notifs.length;
    } catch (e) { /* silencieux */ }
  };

  useEffect(() => {
    const id = setInterval(pollNotifications, 20000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [intRes, statsRes, notifRes, franchisesRes, techRes, missionsRes, donneursRes] = await Promise.all([
        axios.get(`${API}/interventions`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/notifications`),
        axios.get(`${API}/franchises/pending`).catch(() => ({ data: [] })),
        axios.get(`${API}/users/technicians`).catch(() => ({ data: [] })),
        axios.get(`${API}/missions`).catch(() => ({ data: [] })),
        axios.get(`${API}/donneurs-ordre`).catch(() => ({ data: [] }))
      ]);
      setAllInterventions(intRes.data);
      setInterventions(intRes.data);
      setStats(statsRes.data);
      const secNotifs = notifRes.data.filter(n => ['rapport_final_ready', 'new_intervention', 'new_mission'].includes(n.type));
      setNotifications(secNotifs);
      prevNotifCount.current = secNotifs.length;
      setPendingFranchises(franchisesRes.data || []);
      setTechnicians(techRes.data || []);
      setMissions(missionsRes.data || []);
      setDonneursOrdre(donneursRes.data || []);
    } catch (e) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const markFranchiseRecovered = async (interventionId) => {
    try {
      await axios.put(`${API}/interventions/${interventionId}/franchise-recovered`);
      toast.success('Franchise marquée comme récupérée');
      fetchData();
    } catch (e) {
      toast.error('Erreur');
    }
  };

  // Filtrer les interventions
  const getFilteredInterventions = () => {
    let filtered = [...allInterventions];
    
    // Filtre par statut
    if (filterStatus !== 'all') {
      if (filterStatus === 'completed') {
        filtered = filtered.filter(i => i.status === 'validated' && i.rapport_final);
      } else if (filterStatus === 'pending') {
        filtered = filtered.filter(i => i.status === 'submitted');
      } else if (filterStatus === 'draft') {
        filtered = filtered.filter(i => i.status === 'draft' || i.status === 'brouillon');
      } else if (filterStatus === 'in_progress') {
        filtered = filtered.filter(i => i.status === 'in_progress' || (!i.rapport_final && i.status !== 'draft'));
      }
    }
    
    // Filtre par technicien
    if (filterTechnician !== 'all') {
      filtered = filtered.filter(i => i.technician_id === filterTechnician);
    }
    
    // Filtre par date
    if (filterDate !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      if (filterDate === 'today') {
        filtered = filtered.filter(i => {
          const d = new Date(i.created_at);
          return d >= today;
        });
      } else if (filterDate === 'week') {
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        filtered = filtered.filter(i => new Date(i.created_at) >= weekAgo);
      } else if (filterDate === 'month') {
        const monthAgo = new Date(today);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        filtered = filtered.filter(i => new Date(i.created_at) >= monthAgo);
      }
    }
    
    // Recherche textuelle
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(i => 
        i.adresse_complete?.toLowerCase().includes(term) ||
        i.client_nom?.toLowerCase().includes(term) ||
        i.technician_name?.toLowerCase().includes(term) ||
        i.id?.toLowerCase().includes(term) ||
        i.numero_dossier?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const filteredInterventions = getFilteredInterventions();
  
  // Pagination
  const totalPages = Math.ceil(filteredInterventions.length / itemsPerPage);
  const paginatedInterventions = filteredInterventions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats calculés
  const completedCount = allInterventions.filter(i => i.status === 'validated' && i.rapport_final).length;
  const pendingCount = allInterventions.filter(i => i.status === 'submitted').length;
  const inProgressCount = allInterventions.filter(i => !i.rapport_final && i.status !== 'draft').length;
  const draftCount = allInterventions.filter(i => i.status === 'draft' || i.status === 'brouillon').length;

  // Modifier le technicien d'une mission
  const updateMissionTechnician = async (missionId, newTechnicianId) => {
    try {
      await axios.put(`${API}/missions/${missionId}`, { technician_id: newTechnicianId });
      toast.success('Technicien modifié');
      setShowEditMission(false);
      setEditingMission(null);
      fetchData();
    } catch (e) {
      toast.error('Erreur modification');
    }
  };

  const downloadReport = async (intervention, format) => {
    try {
      toast.info(`Génération du ${format.toUpperCase()} en cours...`);
      const response = await axios.post(`${API}/interventions/${intervention.id}/generate-report?format=${format}`, {}, {
        responseType: 'blob'
      });
      const filename = format === 'docx' 
        ? `compte_rendu_${intervention.id.substring(0, 8)}.docx`
        : `compte_rendu_${intervention.id.substring(0, 8)}.pdf`;
      saveAs(response.data, filename);
      toast.success(`${format.toUpperCase()} téléchargé !`);
    } catch (e) {
      toast.error(`Erreur génération ${format.toUpperCase()}`);
    }
  };

  const downloadAllDocuments = async (intervention) => {
    toast.info('Préparation du téléchargement...');
    try {
      const zip = new JSZip();
      let textContent = `KARDIF Investigation - Dossier Complet
============================================
Intervention: ${intervention.adresse_complete}
Date: ${new Date(intervention.created_at).toLocaleDateString('fr-FR')}
Technicien: ${intervention.technician_name}
Client: ${intervention.client_nom || 'N/A'}
Téléphone: ${intervention.client_telephone || 'N/A'}
Numéro dossier: ${intervention.numero_dossier || intervention.id.substring(0, 8)}

Type: ${(intervention.type_intervention || []).join(', ')}
Bâtiment: ${intervention.type_batiment}
Sinistré: ${intervention.sinistre_statut} - ${intervention.nom_sinistre || 'N/A'}

${intervention.franchise ? `FRANCHISE À RÉCUPÉRER: ${intervention.franchise_montant || 'Oui'}` : ''}

--------------------------------------------
RÉSUMÉ DES CONSTATS
--------------------------------------------
${intervention.resume_constats || 'N/A'}

ORIGINE PROBABLE:
${intervention.origine_probable || 'N/A'}

PRÉCONISATIONS:
${intervention.preconisations || 'N/A'}

POINTS URGENTS:
${intervention.points_urgents || 'Aucun'}

--------------------------------------------
RAPPORT FINAL
--------------------------------------------
${intervention.rapport_final || 'N/A'}

${intervention.signed_at ? `Signé le: ${new Date(intervention.signed_at).toLocaleString('fr-FR')}` : ''}

--------------------------------------------
DOCUMENTS COMPLÉMENTAIRES
--------------------------------------------
${(intervention.documents_finaux || []).length > 0 
  ? intervention.documents_finaux.map((d, i) => `${i+1}. ${d.name} (ajouté le ${new Date(d.uploaded_at).toLocaleDateString('fr-FR')})`).join('\n')
  : 'Aucun document complémentaire'}
`;
      zip.file('rapport_complet.txt', textContent);

      const photosFolder = zip.folder('photos');
      const addPhoto = (url, filename) => {
        if (url && url.startsWith('data:')) {
          const base64 = url.split(',')[1];
          photosFolder.file(filename, base64, { base64: true });
        }
      };

      if (intervention.photo_facade) addPhoto(intervention.photo_facade, '01_facade.jpg');
      (intervention.photos_desordres || []).forEach((p, i) => addPhoto(p, `02_desordre_${String(i+1).padStart(2,'0')}.jpg`));
      
      // Photos humidité
      (intervention.mesures_humidite || []).forEach((m, i) => {
        (m.photos || []).forEach((p, j) => addPhoto(p, `03_humidite_${i+1}_${j+1}.jpg`));
      });
      
      // Photos thermiques
      (intervention.mesures_thermiques || []).forEach((m, i) => {
        (m.photos || []).forEach((p, j) => addPhoto(p, `04_thermique_${i+1}_${j+1}.jpg`));
      });
      
      // Photos procédés
      (intervention.procedes || []).forEach((proc, i) => {
        (proc.photos || []).forEach((p, j) => addPhoto(p, `05_procede_${i+1}_${j+1}.jpg`));
      });

      // Anciens formats de photos
      (intervention.photos_mesures_humidite || []).forEach((p, i) => addPhoto(p, `03_humidite_old_${i+1}.jpg`));
      (intervention.photos_camera_thermique || []).forEach((p, i) => addPhoto(p, `04_thermique_old_${i+1}.jpg`));
      (intervention.photos_procedes || []).forEach((p, i) => addPhoto(p, `05_procede_old_${i+1}.jpg`));

      // Signatures
      const signaturesFolder = zip.folder('signatures');
      if (intervention.client_signature) {
        const base64 = intervention.client_signature.split(',')[1];
        signaturesFolder.file('signature_client.png', base64, { base64: true });
      }

      // Documents complémentaires ajoutés par le technicien
      if (intervention.documents_finaux && intervention.documents_finaux.length > 0) {
        const docsFolder = zip.folder('documents_complementaires');
        intervention.documents_finaux.forEach((doc, i) => {
          if (doc.url && doc.url.startsWith('data:')) {
            const base64 = doc.url.split(',')[1];
            const ext = doc.name?.split('.').pop() || 'pdf';
            const safeName = (doc.name || `document_${i+1}`).replace(/[^a-zA-Z0-9._-]/g, '_');
            docsFolder.file(`${String(i+1).padStart(2,'0')}_${safeName}`, base64, { base64: true });
          }
        });
      }

      const content = await zip.generateAsync({ type: 'blob' });
      const filename = `KARDIF_Dossier_${(intervention.adresse_complete || 'intervention').substring(0, 30).replace(/[^a-zA-Z0-9]/g, '_')}.zip`;
      saveAs(content, filename);
      toast.success('Dossier complet téléchargé !');
    } catch (e) {
      console.error(e);
      toast.error('Erreur téléchargement');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const getStatusBadge = (intervention) => {
    if (intervention.status === 'validated' && intervention.rapport_final) {
      return <Badge className="bg-green-100 text-green-800">Terminé</Badge>;
    } else if (intervention.status === 'submitted') {
      return <Badge className="bg-amber-100 text-amber-800">En attente validation</Badge>;
    } else if (intervention.status === 'draft' || intervention.status === 'brouillon') {
      return <Badge className="bg-gray-100 text-gray-800">Brouillon</Badge>;
    } else {
      return <Badge className="bg-blue-100 text-blue-800">En cours</Badge>;
    }
  };

  const clearNotification = async (notifId) => {
    try {
      await axios.delete(`${API}/notifications/${notifId}`);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
    } catch (e) {}
  };

  if (loading) return <MascotLoader text="Chargement des dossiers..." />;

  return (
    <div className={`min-h-screen ${darkMode ? 'dark bg-[#1A1A1A]' : 'bg-[#F8F9FA]'}`}>
      {/* Sidebar */}
      <aside className="fixed top-0 left-0 w-64 h-screen bg-white border-r z-50 hidden md:block">
        <div className="p-6 border-b">
          <img src={LOGO} alt="KARDIF" className="h-12" />
        </div>
        
        <nav className="p-4 space-y-2">
          <a href="#" className="sidebar-link active">
            <FileText className="h-5 w-5" />
            <span>Dossiers</span>
          </a>
          <a href="/planning" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/planning'); }}>
            <Calendar className="h-5 w-5" />
            <span>Planning</span>
          </a>
          <a href="/donneurs-ordre" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/donneurs-ordre'); }}>
            <Building2 className="h-5 w-5" />
            <span>Donneurs d'ordre</span>
          </a>
          <a href="/arch-planner" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/arch-planner'); }}>
            <PenTool className="h-5 w-5" />
            <span>Arch-Planner</span>
          </a>
          <a href="/factures" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/factures'); }} data-testid="nav-factures">
            <Euro className="h-5 w-5" />
            <span>Facturation</span>
          </a>
          <a href="/visio" className="sidebar-link" onClick={(e) => { e.preventDefault(); nav('/visio'); }} data-testid="nav-visio">
            <Video className="h-5 w-5" />
            <span>Visio-Assistance</span>
          </a>
          <a 
            href={`https://mail.google.com/mail/u/?authuser=${user?.email || ''}`} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="sidebar-link"
          >
            <Mail className="h-5 w-5" />
            <span>Ma boîte mail</span>
          </a>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#8B7355] flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">{user?.name}</p>
              <p className="text-xs text-[#6B7280]">Secrétaire</p>
            </div>
          </div>
          <Button variant="outline" className="w-full" onClick={() => { logout(); nav('/login'); }}>
            <LogOut className="h-4 w-4 mr-2" />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="md:ml-64 p-6 md:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <img src={MASCOT_URL} alt="KARDIF" className="hidden sm:block h-14 w-auto drop-shadow mascot-live" data-testid="header-mascot" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Libre Baskerville', serif" }}>
                Espace Secrétariat
              </h1>
              <p className="text-[#6B7280] mt-1">Gestion des dossiers d'intervention</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Theme Toggle */}
            <Button 
              variant="ghost" 
              size="icon"
              onClick={toggleDarkMode}
              title={darkMode ? 'Mode clair' : 'Mode sombre'}
            >
              {darkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
            {notifications.length > 0 && (
              <div className="relative">
                {newNotifPulse && (
                  <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5" data-testid="notif-live-pulse">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-3.5 w-3.5 bg-red-500" />
                  </span>
                )}
                <Badge className={`bg-green-100 text-green-800 ${newNotifPulse ? 'ring-2 ring-red-400 animate-bounce' : 'animate-pulse'}`} data-testid="notif-badge">
                  <Bell className="h-4 w-4 mr-1" />
                  {notifications.length} notification(s)
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Notifications de fin d'intervention */}
        {notifications.length > 0 && (
          <Card className="mb-6 border-green-300 bg-green-50">
            <CardContent className="p-4">
              <h3 className="font-semibold text-green-800 flex items-center gap-2 mb-3">
                <Bell className="h-5 w-5" />
                Notifications - À traiter
              </h3>
              <div className="space-y-2">
                {notifications.map(n => (
                  <div key={n.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                    <div>
                      <p className="font-medium">{n.message}</p>
                      <p className="text-xs text-[#6B7280]">{formatDate(n.created_at)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => nav(`/intervention/${n.intervention_id}`)}>
                        Voir dossier
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => clearNotification(n.id)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stats améliorées */}
        <div className="space-y-6 mb-6">
          {/* Ligne 1: Stats principales */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            <Card className={`cursor-pointer hover:shadow-lg transition-all ${filterStatus === 'all' ? 'ring-2 ring-[#8B7355]' : ''}`} onClick={() => setFilterStatus('all')}>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-[#8B7355]/10 to-[#8B7355]/5">
                <div className="p-3 rounded-xl bg-[#8B7355]">
                  <FileText className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{allInterventions.length}</p>
                  <p className="text-xs text-[#6B7280] font-medium">Total dossiers</p>
                </div>
              </div>
            </Card>
            <Card className={`cursor-pointer hover:shadow-lg transition-all ${filterStatus === 'completed' ? 'ring-2 ring-green-500' : ''}`} onClick={() => setFilterStatus('completed')}>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-green-50 to-green-100/50">
                <div className="p-3 rounded-xl bg-green-500">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-700">{completedCount}</p>
                  <p className="text-xs text-green-600 font-medium">Terminés</p>
                </div>
              </div>
            </Card>
            <Card className={`cursor-pointer hover:shadow-lg transition-all ${filterStatus === 'pending' ? 'ring-2 ring-amber-500' : ''}`} onClick={() => setFilterStatus('pending')}>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-amber-50 to-amber-100/50">
                <div className="p-3 rounded-xl bg-amber-500">
                  <Clock className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{pendingCount}</p>
                  <p className="text-xs text-amber-600 font-medium">En attente</p>
                </div>
              </div>
            </Card>
            <Card className={`cursor-pointer hover:shadow-lg transition-all ${filterStatus === 'in_progress' ? 'ring-2 ring-blue-500' : ''}`} onClick={() => setFilterStatus('in_progress')}>
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-blue-50 to-blue-100/50">
                <div className="p-3 rounded-xl bg-blue-500">
                  <RefreshCw className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-700">{inProgressCount}</p>
                  <p className="text-xs text-blue-600 font-medium">En cours</p>
                </div>
              </div>
            </Card>
            <Card className={`cursor-pointer hover:shadow-lg transition-all ${pendingFranchises.length > 0 ? 'ring-2 ring-green-500 animate-pulse' : ''}`}>
              <div className={`flex items-center gap-3 p-4 ${pendingFranchises.length > 0 ? 'bg-gradient-to-br from-green-100 to-green-200' : 'bg-gradient-to-br from-gray-50 to-gray-100/50'}`}>
                <div className={`p-3 rounded-xl ${pendingFranchises.length > 0 ? 'bg-green-600' : 'bg-gray-400'}`}>
                  <Euro className="h-6 w-6 text-white" />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${pendingFranchises.length > 0 ? 'text-green-700' : 'text-gray-600'}`}>{pendingFranchises.length}</p>
                  <p className={`text-xs font-medium ${pendingFranchises.length > 0 ? 'text-green-600' : 'text-gray-500'}`}>Franchises à récupérer</p>
                </div>
              </div>
            </Card>
          </div>
          
          {/* Ligne 2: Résumé détaillé */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Activité par période */}
            <Card className="p-4 border-[#8B7355]/20">
              <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#8B7355]" />
                Activité par période
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-green-50 to-white rounded-lg border-l-4 border-green-500">
                  <span className="text-sm font-medium text-[#1A1A1A]">Aujourd'hui</span>
                  <span className="text-lg font-bold text-green-600">
                    {allInterventions.filter(i => {
                      const today = new Date();
                      const d = new Date(i.created_at);
                      return d.toDateString() === today.toDateString();
                    }).length}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border-l-4 border-blue-500">
                  <span className="text-sm font-medium text-[#1A1A1A]">Cette semaine</span>
                  <span className="text-lg font-bold text-blue-600">
                    {allInterventions.filter(i => {
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return new Date(i.created_at) >= weekAgo;
                    }).length}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gradient-to-r from-[#8B7355]/10 to-white rounded-lg border-l-4 border-[#8B7355]">
                  <span className="text-sm font-medium text-[#1A1A1A]">Ce mois</span>
                  <span className="text-lg font-bold text-[#8B7355]">
                    {allInterventions.filter(i => {
                      const monthAgo = new Date();
                      monthAgo.setMonth(monthAgo.getMonth() - 1);
                      return new Date(i.created_at) >= monthAgo;
                    }).length}
                  </span>
                </div>
              </div>
            </Card>
            
            {/* Répartition par technicien */}
            <Card className="p-4 border-[#8B7355]/20">
              <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Users className="h-5 w-5 text-[#8B7355]" />
                Par technicien
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {technicians.length > 0 ? technicians.map(tech => {
                  const count = allInterventions.filter(i => i.technician_id === tech.id).length;
                  const percentage = allInterventions.length > 0 ? Math.round(count / allInterventions.length * 100) : 0;
                  return (
                    <div key={tech.id} className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#8B7355] flex items-center justify-center text-white text-xs font-bold">
                        {tech.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-medium truncate">{tech.name}</span>
                          <span className="text-[#6B7280]">{count}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="h-full bg-[#8B7355] rounded-full" style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-sm text-[#6B7280] text-center py-4">Aucun technicien</p>
                )}
              </div>
            </Card>
            
            {/* Actions rapides */}
            <Card className="p-4 border-[#8B7355]/20">
              <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-[#8B7355]" />
                Actions rapides
              </h3>
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-[#8B7355]/30 hover:bg-[#8B7355]/10"
                  onClick={() => nav('/planning')}
                >
                  <Calendar className="h-4 w-4 mr-2 text-[#8B7355]" />
                  Voir le planning
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-[#8B7355]/30 hover:bg-[#8B7355]/10"
                  onClick={() => nav('/donneurs-ordre')}
                >
                  <Building2 className="h-4 w-4 mr-2 text-[#8B7355]" />
                  Donneurs d'ordre
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start border-[#8B7355]/30 hover:bg-[#8B7355]/10"
                  onClick={() => nav('/search')}
                >
                  <Search className="h-4 w-4 mr-2 text-[#8B7355]" />
                  Recherche avancée
                </Button>
                {missions.length > 0 && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700"
                    onClick={() => setActiveTab('missions')}
                  >
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {missions.length} mission(s) planifiée(s)
                  </Button>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* ALERTE FRANCHISES */}
        {pendingFranchises.length > 0 && (
          <Card className="mb-6 border-2 border-green-400 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-green-800 flex items-center gap-2">
                  <Euro className="h-5 w-5" />
                  Franchises à récupérer ({pendingFranchises.length})
                </h3>
              </div>
              <div className="space-y-2">
                {pendingFranchises.slice(0, 3).map(f => (
                  <div key={f.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-200">
                    <div>
                      <p className="font-medium">{f.adresse_complete || 'N/A'}</p>
                      <p className="text-sm text-[#6B7280]">
                        Client: {f.client_nom || 'N/A'} • Montant: <span className="font-bold text-green-700">{f.franchise_montant || 'N/A'}</span>
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => nav(`/intervention/${f.id}`)}>Voir</Button>
                      <Button size="sm" className="bg-green-600 text-white" onClick={() => markFranchiseRecovered(f.id)}>
                        <CheckCircle className="h-4 w-4 mr-1" />Récupérée
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filtres et recherche */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[250px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9CA3AF]" />
                  <Input
                    placeholder="Rechercher par adresse, client, technicien ou n° dossier..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="completed">Terminés</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="draft">Brouillons</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterTechnician} onValueChange={(v) => { setFilterTechnician(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Technicien" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Tous les techniciens</SelectItem>
                  {technicians.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={filterDate} onValueChange={(v) => { setFilterDate(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Période" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">Toutes dates</SelectItem>
                  <SelectItem value="today">Aujourd'hui</SelectItem>
                  <SelectItem value="week">Cette semaine</SelectItem>
                  <SelectItem value="month">Ce mois</SelectItem>
                </SelectContent>
              </Select>
              
              {(filterStatus !== 'all' || filterTechnician !== 'all' || filterDate !== 'all' || searchTerm) && (
                <Button variant="ghost" onClick={() => { setFilterStatus('all'); setFilterTechnician('all'); setFilterDate('all'); setSearchTerm(''); setCurrentPage(1); }}>
                  <X className="h-4 w-4 mr-1" />
                  Effacer
                </Button>
              )}
            </div>
            
            <p className="text-sm text-[#6B7280] mt-3">
              {filteredInterventions.length} dossier(s) trouvé(s)
            </p>
          </CardContent>
        </Card>

        {/* Liste des dossiers */}
        {loading ? (
          <div className="flex justify-center py-12"><div className="spinner" /></div>
        ) : filteredInterventions.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FolderOpen className="h-12 w-12 text-[#D4C5A9] mx-auto mb-4" />
              <p className="text-[#6B7280]">Aucun dossier trouvé</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {paginatedInterventions.map((intervention) => (
                <Card key={intervention.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-[#8B7355] flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-[#1A1A1A]">{intervention.adresse_complete || 'Adresse non renseignée'}</h3>
                              {getStatusBadge(intervention)}
                              {intervention.franchise && (
                                <Badge className="bg-amber-100 text-amber-800">
                                  <Euro className="h-3 w-3 mr-1" />
                                  {intervention.franchise_montant || 'Franchise'}
                                </Badge>
                              )}
                            </div>
                            <div className="text-sm text-[#6B7280] mt-1 space-y-1">
                              <p>
                                <span className="font-medium">Technicien:</span> {intervention.technician_name} • 
                                <span className="font-medium"> Date:</span> {formatDate(intervention.created_at)}
                              </p>
                              {intervention.client_nom && (
                                <p><span className="font-medium">Client:</span> {intervention.client_nom} {intervention.client_telephone && `• ${intervention.client_telephone}`}</p>
                              )}
                              <p className="text-xs text-[#9CA3AF]">
                                N° dossier: {intervention.numero_dossier || intervention.id.substring(0, 8)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => nav(`/intervention/${intervention.id}`)}>
                          <Eye className="h-4 w-4 mr-1" />
                          Voir
                        </Button>
                        {intervention.rapport_final && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => downloadReport(intervention, 'pdf')}>
                              <FileDown className="h-4 w-4 mr-1" />
                              PDF
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => downloadReport(intervention, 'docx')}>
                              <FileText className="h-4 w-4 mr-1" />
                              Word
                            </Button>
                          </>
                        )}
                        <Button size="sm" className="bg-[#8B7355] hover:bg-[#7A6548] text-white" onClick={() => downloadAllDocuments(intervention)}>
                          <Download className="h-4 w-4 mr-1" />
                          ZIP
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-4 mt-6">
                <Button 
                  variant="outline" 
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => p - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-[#6B7280]">
                  Page {currentPage} sur {totalPages}
                </span>
                <Button 
                  variant="outline" 
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => p + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}

        {/* Missions planifiées avec possibilité de changer le technicien */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-[#8B7355]" />
              Missions planifiées ({missions.filter(m => m.status === 'scheduled').length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {missions.filter(m => m.status === 'scheduled').length === 0 ? (
              <p className="text-[#6B7280] text-center py-4">Aucune mission planifiée</p>
            ) : (
              <div className="space-y-3">
                {missions.filter(m => m.status === 'scheduled').slice(0, 5).map(mission => (
                  <div key={mission.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">{mission.title}</p>
                      <p className="text-sm text-[#6B7280]">
                        {mission.scheduled_date} • {mission.scheduled_time_start} - {mission.scheduled_time_end}
                      </p>
                      <p className="text-sm text-[#6B7280]">
                        Technicien: <span className="font-medium">{mission.technician_name}</span>
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setEditingMission(mission); setShowEditMission(true); }}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Modifier
                    </Button>
                  </div>
                ))}
                {missions.filter(m => m.status === 'scheduled').length > 5 && (
                  <Button variant="link" className="w-full" onClick={() => nav('/planning')}>
                    Voir toutes les missions dans le planning
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Nouveau dossier (pré-remplissage par la secrétaire) */}
      <Dialog open={showNewDossier} onOpenChange={setShowNewDossier}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle>Nouveau dossier d'intervention</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7280] -mt-2">Notez les informations du sinistre. Le dossier pré-rempli sera transmis au technicien pour qu'il complète l'intervention.</p>
          <div className="space-y-4 mt-3">
            <div>
              <Label>Technicien assigné *</Label>
              <Select value={newDossier.technician_id} onValueChange={(v) => setNewDossier({ ...newDossier, technician_id: v })}>
                <SelectTrigger data-testid="dossier-tech-select"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent className="bg-white border shadow-lg">
                  {technicians.map(t => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom du sinistré</Label>
                <Input value={newDossier.nom_sinistre} onChange={(e) => setNewDossier({ ...newDossier, nom_sinistre: e.target.value })} placeholder="M. Dupont" data-testid="dossier-sinistre-input" />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={newDossier.client_telephone} onChange={(e) => setNewDossier({ ...newDossier, client_telephone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Adresse</Label>
              <Input value={newDossier.adresse_complete} onChange={(e) => setNewDossier({ ...newDossier, adresse_complete: e.target.value })} placeholder="Adresse complète" data-testid="dossier-address-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>N° de sinistre</Label>
                <Input value={newDossier.numero_sinistre} onChange={(e) => setNewDossier({ ...newDossier, numero_sinistre: e.target.value })} placeholder="SIN-2026-778" data-testid="dossier-numero-sinistre-input" />
              </div>
              <div>
                <Label>N° de dossier</Label>
                <Input value={newDossier.numero_dossier} onChange={(e) => setNewDossier({ ...newDossier, numero_dossier: e.target.value })} placeholder="DOS-99812" />
              </div>
            </div>
            <div>
              <Label>Assurance</Label>
              <Input value={newDossier.nom_assurance} onChange={(e) => setNewDossier({ ...newDossier, nom_assurance: e.target.value })} placeholder="Ex: AXA Assurances" data-testid="dossier-assurance-input" />
            </div>
            <div>
              <Label>Donneur d'ordre</Label>
              <Select value={newDossier.donneur_ordre_id || 'none'} onValueChange={(v) => setNewDossier({ ...newDossier, donneur_ordre_id: v === 'none' ? '' : v })}>
                <SelectTrigger data-testid="dossier-donneur-select"><SelectValue placeholder="Sélectionner (optionnel)" /></SelectTrigger>
                <SelectContent className="bg-white border shadow-lg">
                  <SelectItem value="none">Aucun</SelectItem>
                  {donneursOrdre.map(d => (<SelectItem key={d.id} value={d.id}>{d.nom}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes / consignes (optionnel)</Label>
              <Input value={newDossier.resume_constats} onChange={(e) => setNewDossier({ ...newDossier, resume_constats: e.target.value })} placeholder="Informations utiles pour le technicien" />
            </div>
            <div>
              <Label className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#8B7355]" />Documents PDF à faire signer (optionnel)</Label>
              <p className="text-xs text-[#6B7280] mb-2">Ces PDF seront accessibles au technicien qui pourra les faire signer au client pendant l'intervention.</p>
              {(newDossier.pieces_jointes || []).length > 0 && (
                <ul className="space-y-1 mb-2" data-testid="dossier-attachments-list">
                  {newDossier.pieces_jointes.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2 p-2 rounded border bg-gray-50 text-sm">
                      <span className="flex items-center gap-2 min-w-0"><FileText className="h-4 w-4 text-red-600" /><span className="truncate">{a.name}</span></span>
                      <button type="button" onClick={() => setNewDossier(d => ({ ...d, pieces_jointes: d.pieces_jointes.filter(p => p.id !== a.id) }))} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></button>
                    </li>
                  ))}
                </ul>
              )}
              <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-[#8B7355]/30 rounded-lg cursor-pointer hover:bg-[#8B7355]/5">
                <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="dossier-attachment-input" disabled={uploadingDossierPJ}
                  onChange={async (e) => {
                    const file = e.target.files?.[0]; e.target.value = '';
                    if (!file) return;
                    setUploadingDossierPJ(true);
                    try {
                      const fd = new FormData(); fd.append('file', file);
                      const res = await axios.post(`${API}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
                      const att = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: file.name, url: res.data.url, size: file.size, uploaded_by_name: 'Secrétariat', uploaded_at: new Date().toISOString() };
                      setNewDossier(d => ({ ...d, pieces_jointes: [...(d.pieces_jointes || []), att] }));
                    } catch (err) { toast.error('Échec du téléversement'); }
                    setUploadingDossierPJ(false);
                  }} />
                <Upload className="h-5 w-5 text-[#8B7355]" />
                <span className="text-sm text-[#6B7280]">{uploadingDossierPJ ? 'Envoi...' : 'Ajouter un PDF'}</span>
              </label>
            </div>
            <Button onClick={handleCreateDossier} disabled={creatingDossier} className="w-full bg-[#8B7355] hover:bg-[#7A6548] text-white" data-testid="save-dossier-btn">
              {creatingDossier ? 'Création...' : 'Créer et transmettre au technicien'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Dialog modification mission */}
      <Dialog open={showEditMission} onOpenChange={setShowEditMission}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Modifier la mission</DialogTitle>
          </DialogHeader>
          {editingMission && (
            <div className="space-y-4 mt-4">
              <div>
                <Label>Mission</Label>
                <p className="font-medium">{editingMission.title}</p>
                <p className="text-sm text-[#6B7280]">{editingMission.address}</p>
              </div>
              <div>
                <Label>Changer le technicien</Label>
                <Select 
                  value={editingMission.technician_id} 
                  onValueChange={(v) => setEditingMission({...editingMission, technician_id: v})}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {technicians.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="w-full bg-[#8B7355] hover:bg-[#7A6548] text-white"
                onClick={() => updateMissionTechnician(editingMission.id, editingMission.technician_id)}
              >
                Enregistrer
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SecretaryDashboard;
